import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, ReplaySubject } from 'rxjs';
import { StorageService } from './storage.service';
import { SessionService } from './session.service';

/**
 * ReauthService — coordina la ri-autenticazione IN-PLACE dopo un 401, senza reload.
 *
 * Scopo: quando il token scade mentre l'utente sta lavorando, invece di sloggarlo
 * (perdendo il lavoro non salvato) apriamo una modale password-only, rinnoviamo il
 * token e RIGIOCHIAMO le richieste fallite. La pagina non viene mai ricaricata.
 *
 * Design anti-ciclo DI: questo service NON dipende da HttpClient/ApiService (che
 * dipendono dall'interceptor). È un puro coordinatore di stato. La chiamata di login
 * la fa il ReauthModalComponent, che poi invoca completeReauth() qui.
 *
 * Single-flight: se 5 chiamate scadono insieme (es. i poll di unread-count), la prima
 * apre la modale e crea lo stream condiviso `token$`; le altre ricevono lo STESSO stream
 * e vengono tutte rigiocate con il nuovo token dopo un solo re-login.
 */
@Injectable({ providedIn: 'root' })
export class ReauthService {

  /** Visibilità della modale — il ReauthModalComponent si sottoscrive qui. */
  readonly visible$ = new BehaviorSubject<boolean>(false);

  /** Username fissato alla sessione corrente (re-auth solo password). */
  pinnedUsername = '';

  private refreshing = false;
  private token$: ReplaySubject<string> | null = null;

  constructor(
    private storage: StorageService,
    private session: SessionService
  ) {}

  /**
   * Chiamata dall'interceptor su un 401. Single-flight: la prima invocazione apre la
   * modale e crea lo stream condiviso; le 401 concorrenti ricevono lo stesso stream e
   * vengono rigiocate tutte insieme dopo il re-login. Emette il nuovo JWT al successo,
   * va in errore se l'utente annulla (→ logout).
   */
  requestReauth(): Observable<string> {
    if (!this.refreshing) {
      this.refreshing = true;
      this.token$ = new ReplaySubject<string>(1);
      this.pinnedUsername = this.storage.getUserStorage()?.username || '';
      this.visible$.next(true);
    }
    return this.token$!.asObservable();
  }

  /**
   * Chiamata dal modale dopo un login riuscito. Sostituisce SOLO il JWT nello storage
   * (chiave di cifratura invariata → storage coerente), nasconde la modale e rilascia
   * tutte le richieste in coda con il nuovo token.
   */
  completeReauth(newToken: string): void {
    try {
      const user = this.storage.getUserStorage();
      if (user && newToken) {
        user.token = newToken;
        this.storage.setUserStorage(user);
      }
    } catch (e) {
      console.log('🔐 [Reauth] token swap fallito:', e);
    }
    this.visible$.next(false);
    this.refreshing = false;
    if (this.token$) {
      this.token$.next(newToken);
      this.token$.complete();
      this.token$ = null;
    }
  }

  /**
   * Chiamata dal "Sign out instead" del modale (o dopo troppi tentativi falliti).
   * Interrompe le richieste in coda e ricade sul flusso di logout esistente
   * (pulizia sessione + notice "Session expired" sulla pagina di login).
   */
  cancelToLogin(): void {
    this.visible$.next(false);
    this.refreshing = false;
    if (this.token$) {
      this.token$.error(new Error('reauth_cancelled'));
      this.token$ = null;
    }
    this.session.handleUnauthorized();
  }
}
